import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { Coins, TrendingDown, TrendingUp, CreditCard, AlertTriangle, Clock, Crown, Zap, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";
import { creditsService, type CreditsOverview, type BillingSubscription } from "@/services/creditsService";

const CATEGORY_COLORS: Record<string, string> = {
  chat: "hsl(var(--chart-1))",
  import: "hsl(var(--chart-2))",
  export: "hsl(var(--chart-3))",
  other: "hsl(var(--chart-4))"
};

const CATEGORY_LABELS: Record<string, string> = {
  chat: "AI Chat",
  import: "Imports",
  export: "Exports",
  other: "Other"
};

function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "MMM d, h:mm a");
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
}

interface CreditsDashboardProps {
  hideHeader?: boolean;
  showPlanSelector?: boolean;
}

export default function CreditsDashboard({ hideHeader = false, showPlanSelector = false }: CreditsDashboardProps) {
  const { data, isLoading, error } = useQuery<CreditsOverview>({
    queryKey: ["credits-overview"],
    queryFn: () => creditsService.getCreditsOverview(),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  const { data: billingData } = useQuery<BillingSubscription>({
    queryKey: ["billing-subscription"],
    queryFn: () => creditsService.getBillingSubscription(),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              Failed to load credits data. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pieData = Object.entries(data.usageByCategory)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: CATEGORY_LABELS[key] || key,
      value,
      color: CATEGORY_COLORS[key] || CATEGORY_COLORS.other
    }));

  const barData = data.monthlyUsage.map(item => ({
    date: format(new Date(item.date), "MMM d"),
    credits: item.credits
  }));

  return (
    <div className={hideHeader ? "space-y-6" : "p-6 space-y-6 overflow-auto h-full"}>
      {!hideHeader && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Credits Dashboard</h1>
            <p className="text-muted-foreground">Monitor your credit usage and balance</p>
          </div>
          <Link href="/pricing">
            <Button variant="default" data-testid="button-buy-credits">
              <CreditCard className="h-4 w-4 mr-2" />
              Upgrade Plan
            </Button>
          </Link>
        </div>
      )}

      {billingData && (
        <Card data-testid="card-subscription-status">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Crown className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{billingData.tierName} Plan</CardTitle>
                <CardDescription>
                  {billingData.plan?.price && parseInt(billingData.plan.price) > 0 
                    ? `$${(parseInt(billingData.plan.price) / 100).toFixed(0)}/month`
                    : "Free forever"}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {billingData.isCancelled ? (
                <Badge variant="secondary">Cancelled</Badge>
              ) : billingData.cancelAtPeriodEnd ? (
                <Badge variant="secondary">Cancels at period end</Badge>
              ) : (
                <Badge variant="default">Active</Badge>
              )}
              {billingData.tierName.toLowerCase() !== 'beast' && (
                <Link href="/pricing">
                  <Button variant="outline" size="sm" data-testid="button-upgrade">
                    <Zap className="h-4 w-4 mr-1" />
                    Upgrade
                    <ArrowUpRight className="h-3 w-3 ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Credits</p>
                <p className="text-xl font-semibold" data-testid="text-monthly-credits">
                  {formatNumber(billingData.monthlyCredits)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Next Billing</p>
                <p className="text-xl font-semibold" data-testid="text-next-billing">
                  {billingData.nextBillingDate 
                    ? format(new Date(billingData.nextBillingDate), "MMM d, yyyy")
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-xl font-semibold capitalize" data-testid="text-subscription-status">
                  {billingData.subscription?.status || "Active"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {data.isLowBalance && !data.isDepleted && (
        <Card className="border-yellow-500/50 bg-yellow-500/10" data-testid="card-low-balance-warning">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm">Your credit balance is running low. Consider purchasing more credits to avoid interruptions.</span>
          </CardContent>
        </Card>
      )}

      {data.isDepleted && (
        <Card className="border-destructive/50 bg-destructive/10" data-testid="card-depleted-warning">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-sm">Your credits are depleted. Please purchase more credits to continue using AI features.</span>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="card-balance">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Balance</CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-balance">
              {data.unlimitedAiCredits ? "Unlimited" : formatNumber(data.balance)}
            </div>
            <p className="text-xs text-muted-foreground">
              credits available
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-total-earned">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credits Available</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-total-earned">
              {formatNumber(data.totalEarned)}
            </div>
            <p className="text-xs text-muted-foreground">
              from purchases and bonuses
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-total-spent">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <TrendingDown className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-total-spent">
              {formatNumber(data.totalSpent)}
            </div>
            <p className="text-xs text-muted-foreground">
              on AI usage
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-monthly-usage">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Usage</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-percentage-used">
              {data.percentageUsed}%
            </div>
            <Progress 
              value={data.percentageUsed} 
              className="mt-2"
              data-testid="progress-monthly-usage"
            />
            <p className="text-xs text-muted-foreground mt-1">
              of {formatNumber(data.monthlyAllowance)} monthly allowance
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card data-testid="card-usage-chart">
          <CardHeader>
            <CardTitle>Daily Usage (Last 30 Days)</CardTitle>
            <CardDescription>Credits used per day</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatNumber(value), "Credits"]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Bar 
                    dataKey="credits" 
                    fill="hsl(var(--primary))" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-category-chart">
          <CardHeader>
            <CardTitle>Usage by Category</CardTitle>
            <CardDescription>Where your credits are being spent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [formatNumber(value), "Credits"]}
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No usage data yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="card-transactions">
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
          <CardDescription>Last 20 credit transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {data.transactions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No transactions yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance After</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((tx) => (
                  <TableRow key={tx.id} data-testid={`row-transaction-${tx.id}`}>
                    <TableCell className="text-muted-foreground">
                      {formatDate(tx.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={tx.type === "credit" ? "default" : "secondary"}
                      >
                        {tx.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">
                      {tx.reason.replace(/_/g, " ")}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${
                      parseInt(tx.amount) >= 0 ? "text-green-600" : ""
                    }`}>
                      {parseInt(tx.amount) >= 0 ? "+" : ""}{tx.amount}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {tx.balanceAfter}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
